import { getChain } from "@gearbox-protocol/sdk";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

const ChainlistEntriesSchema = z.array(
  z.object({
    name: z.string().min(1),
    chainId: z
      .union([z.number(), z.string()])
      .transform(v => (typeof v === "string" ? Number(v) : v))
      .refine(n => Number.isFinite(n), "Invalid chainId"),
  }),
);

export function useChainlist() {
  return useQuery<Map<number, string>, Error>({
    queryKey: ["chainlist"],
    queryFn: async () => {
      const res = await fetch("https://chainlist.org/rpcs.json");
      if (!res.ok)
        throw new Error(`Failed to fetch chainlist.org: ${res.status}`);
      const raw: unknown = await res.json();
      const entries = ChainlistEntriesSchema.parse(raw);

      const map = new Map<number, string>();
      for (const e of entries) map.set(e.chainId, e.name);
      return map;
    },
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 7 * 24 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: 2,
  });
}

export const useChainName = (chainId: number) => {
  const { data: chainMap } = useChainlist();

  if (chainId === 0) return "All chains";

  try {
    return getChain(chainId).name;
  } catch {
    if (chainMap?.has(chainId)) {
      return chainMap.get(chainId)!;
    }
    return `Chain ${chainId}`;
  }
};
